import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

type Eshop = {
  id: number;
  code: string;
  name: string;
  country: string;
  base_url: string | null;
  currency: string | null;
  /** Název Cloudflare secretu, ve kterém je klíč uložený. */
  token_env: string | null;
  /** Je secret v Cloudflare reálně nastavený? Hodnota se nikdy neposílá. */
  has_token: boolean;
  active: number;
  products_count: number;
};

/**
 * Napojení e-shopů — evidence e-shopů a jejich API přístupů.
 *
 * API klíče žijí v Cloudflare secrets, ne v databázi. Tady se eviduje jen
 * název secretu a UI ukazuje, jestli je nastavený.
 */
export function EshopyPage() {
  const [eshops, setEshops] = useState<Eshop[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Eshop | 'new' | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiFetch<{ eshops: Eshop[] }>('/api/eshops');
      setEshops(data.eshops);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleActive(shop: Eshop) {
    try {
      await apiFetch(`/api/eshops/${shop.id}`, {
        method: 'PATCH',
        body: { active: !shop.active },
      });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Změna selhala', kind: 'error' });
    }
  }

  async function remove(shop: Eshop) {
    if (!confirm(`Opravdu smazat e-shop ${shop.name}?`)) return;
    try {
      await apiFetch(`/api/eshops/${shop.id}`, { method: 'DELETE' });
      setToast({ msg: 'E-shop smazán', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Napojení e-shopů</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            E-shopy, se kterými systém pracuje, a jejich API přístupy. Klíče se ukládají zašifrovaně
            a zpět se nezobrazují — jen poslední znaky pro kontrolu.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
        >
          Přidat e-shop
        </button>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg">
              <Th>E-shop</Th>
              <Th>Kód</Th>
              <Th>Země</Th>
              <Th>Měna</Th>
              <Th>API klíč</Th>
              <Th right>Produktů</Th>
              <Th>Stav</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody>
            {eshops == null ? (
              <tr>
                <td colSpan={8} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : eshops.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-8 text-center text-text-muted">
                  Zatím žádné e-shopy. Klikni „Přidat e-shop".
                </td>
              </tr>
            ) : (
              eshops.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-border-light hover:bg-surface-hover transition-colors"
                >
                  <td className="px-5 py-3">
                    <div className="text-text font-medium">{s.name}</div>
                    {s.base_url && (
                      <div className="text-xs text-text-muted truncate max-w-[220px]">
                        {s.base_url}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-text-secondary font-mono text-xs">{s.code}</td>
                  <td className="px-5 py-3 text-text-secondary">{s.country}</td>
                  <td className="px-5 py-3 text-text-secondary">{s.currency ?? '—'}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                          s.has_token
                            ? 'text-success bg-success-light'
                            : 'text-warning bg-warning-light'
                        }`}
                      >
                        {s.has_token ? 'nastaven' : 'chybí v Cloudflare'}
                      </span>
                    </div>
                    {s.token_env && (
                      <div className="text-[11px] text-text-muted font-mono mt-0.5">
                        {s.token_env}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-text-secondary">{s.products_count}</td>
                  <td className="px-5 py-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(s)}
                      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded cursor-pointer ${
                        s.active ? 'text-success bg-success-light' : 'text-text-secondary bg-bg'
                      }`}
                    >
                      {s.active ? 'Aktivní' : 'Neaktivní'}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setEditing(s)}
                      className="text-sm font-semibold text-primary hover:underline cursor-pointer"
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(s)}
                      className="text-sm font-semibold text-danger hover:underline cursor-pointer ml-4"
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EshopModal
          eshop={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null);
            setToast({ msg, kind: 'success' });
            load();
          }}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function EshopModal({
  eshop,
  onClose,
  onSaved,
}: {
  eshop: Eshop | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const isNew = eshop === null;
  const [code, setCode] = useState(eshop?.code ?? '');
  const [name, setName] = useState(eshop?.name ?? '');
  const [country, setCountry] = useState(eshop?.country ?? 'CZ');
  const [baseUrl, setBaseUrl] = useState(eshop?.base_url ?? '');
  const [currency, setCurrency] = useState(eshop?.currency ?? 'CZK');
  const [tokenEnv, setTokenEnv] = useState(eshop?.token_env ?? '');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        country: country.trim(),
        base_url: baseUrl.trim() || null,
        currency: currency.trim() || null,
        // Jen název Cloudflare secretu — hodnota klíče sem nikdy nevstupuje.
        token_env: tokenEnv.trim() || null,
      };
      if (isNew) {
        await apiFetch('/api/eshops', { method: 'POST', body: { ...body, code: code.trim() } });
      } else {
        await apiFetch(`/api/eshops/${eshop.id}`, { method: 'PATCH', body });
      }
      onSaved(isNew ? 'E-shop přidán' : 'E-shop upraven');
    } catch (e: unknown) {
      setLocalError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg bg-surface rounded-xl p-6 shadow-modal reveal max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-text mb-5">
          {isNew ? 'Přidat e-shop' : `Upravit ${eshop.name}`}
        </h2>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Název" htmlFor="es-name">
              <input
                id="es-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="V-Mart.sk"
                className={inputCls}
              />
            </Field>
            <Field
              label="Kód"
              htmlFor="es-code"
              hint={isNew ? 'např. v-mart-sk' : 'kód nejde měnit'}
            >
              <input
                id="es-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={!isNew}
                placeholder="v-mart-sk"
                className={`${inputCls} ${isNew ? '' : 'bg-bg text-text-muted'}`}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Země" htmlFor="es-country">
              <select
                id="es-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className={inputCls}
              >
                <option value="CZ">CZ</option>
                <option value="SK">SK</option>
              </select>
            </Field>
            <Field label="Měna" htmlFor="es-currency">
              <select
                id="es-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={inputCls}
              >
                <option value="CZK">CZK</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
          </div>

          <Field label="URL e-shopu" htmlFor="es-url">
            <input
              id="es-url"
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://www.v-mart.sk"
              className={inputCls}
            />
          </Field>

          <Field
            label="Název Cloudflare secretu s API klíčem"
            htmlFor="es-token-env"
            hint="prázdné = odvodí se ze země"
          >
            <input
              id="es-token-env"
              type="text"
              value={tokenEnv}
              onChange={(e) => setTokenEnv(e.target.value)}
              placeholder={`SHOPTET_API_TOKEN_${country}`}
              className={`${inputCls} font-mono`}
            />
          </Field>

          <div className="text-xs text-text-secondary bg-bg rounded-lg px-3 py-2.5 leading-relaxed">
            Samotný klíč se sem <strong>nezadává</strong> — je uložený v Cloudflare, ne v naší
            databázi. Nahraješ ho příkazem:
            <code className="block mt-1 font-mono text-[11px] text-text break-all">
              npx wrangler pages secret put {tokenEnv.trim() || `SHOPTET_API_TOKEN_${country}`}{' '}
              --project-name sklad-sync
            </code>
          </div>

          {localError && <div className="text-sm text-danger">{localError}</div>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-6 py-2 transition-colors cursor-pointer"
            >
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  'w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-text mb-1.5">
        {label}
        {hint && <span className="text-text-muted font-normal"> — {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-5 py-3 text-[11px] font-semibold text-text-muted uppercase tracking-wider ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
